<template>
  <div class="editor-container">
    <div class="grey lighten-4 editor-container__tools">
      <v-btn-toggle
        v-if="editor"
        dense
        group
        borderless
        background-color="transparent"
      >
        <v-btn
          icon
          :input-value="editor.isActive('bold')"
          small
          @click="editor.chain().focus().toggleBold().run()"
        >
          <v-icon x-small>
            fa fa-bold
          </v-icon>
        </v-btn>
        <v-btn
          icon
          :input-value="editor.isActive('italic')"
          small
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <v-icon x-small>
            fa fa-italic
          </v-icon>
        </v-btn>
        <v-btn
          icon
          :input-value="editor.isActive('underline')"
          small
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <v-icon x-small>
            fa fa-underline
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :input-value="editor.isActive('strike')"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <v-icon x-small>
            fa fa-strikethrough
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :input-value="editor.isActive('blockquote')"
          @click="editor.chain().focus().toggleBlockquote().run()"
        >
          <v-icon x-small>
            fa fa-quote-left
          </v-icon>
        </v-btn>
        <v-divider vertical />
        <v-menu
          v-model="linkMenu"
          :close-on-content-click="false"
          bottom
          nudge-bottom="30px"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              @click="openLinkMenu()"
              v-on="on"
            >
              <v-icon
                x-small
              >
                fa fa-link
              </v-icon>
            </v-btn>
          </template>
          <v-card class="pa-2">
            <form @submit.prevent="link !== '' ? setLink() : undefined">
              <v-row
                dense
                align="center"
              >
                <v-col>
                  <v-text-field
                    v-model="link"
                    dense
                    :placeholder="$t('text_input.enter_url.placeholder')"
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="grey lighten-4"
                    elevation="0"
                    fab
                    tile
                    small
                    @click="setLink()"
                  >
                    <v-icon x-small>
                      fa fa-check
                    </v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="grey lighten-4"
                    elevation="0"
                    fab
                    tile
                    small
                    @click="unsetLink()"
                  >
                    <v-icon x-small>
                      fa fa-times
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </form>
          </v-card>
        </v-menu>
        <v-divider vertical />
        <v-btn
          icon
          small
          :input-value="editor.isActive('bulletList')"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <v-icon x-small>
            fa fa-list
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :input-value="editor.isActive('orderedList')"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <v-icon x-small>
            fa fa-list-ol
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :input-value="editor.isActive('codeBlock')"
          @click="editor.chain().focus().toggleCodeBlock().run()"
        >
          <v-icon x-small>
            fa fa-code
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          @click="editor.chain().focus().setHorizontalRule().run()"
        >
          <v-icon x-small>
            fa fa-horizontal-rule
          </v-icon>
        </v-btn>
      </v-btn-toggle>
    </div>
    <editor-content :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { Placeholder } from '@tiptap/extension-placeholder'
import { Link } from '@tiptap/extension-link'
import i18n from '@/plugins/i18n'

export default {
  name: 'RichTextEditor',
  components: {
    EditorContent
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: i18n.t('text_input.notes.placeholder')
    }
  },

  data() {
    return {
      editor: null,
      linkMenu: false,
      link: ''
    }
  },

  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
    }
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        Placeholder.configure({
          placeholder: this.placeholder
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true
        })
      ],
      onUpdate: () => {
        // HTML
        this.$emit('input', this.editor.getHTML())

        // JSON
        // this.$emit('input', this.editor.getJSON())
      }
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },

  methods: {
    openLinkMenu() {
      this.linkMenu = true

      if (this.editor.isActive('link')) {
        this.link = this.editor.getAttributes('link').href
      } else {
        this.link = ''
      }
    },
    setLink() {
      if (this.link === '') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run()

        this.linkMenu = false

        return
      }

      let link = this.link

      if (!link.startsWith('http://') && !link.startsWith('https://')) {
        link = 'http://' + link
      }

      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: link })
        .run()

      this.linkMenu = false
      this.link = ''
    },
    unsetLink() {
      if (this.editor.isActive('link')) {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run()
      }

      this.link = ''
      this.linkMenu = false
    }
  }
}
</script>

<style lang="scss">
$color-black: #000000;
$color-white: #ffffff;

.editor-container {
  border: 1px solid currentColor;
  border-radius: 6px;

  .editor-container__tools {
    border-top-right-radius: 6px;
    border-top-left-radius: 6px;
    overflow: hidden;
  }
}

.rich-text {
  font-size: 14px;
  color: #2a2a2a;

  blockquote {
    padding: 5px 12px;
    border-left: 4px solid #e2e2e2;
    margin-bottom: 10px;

    p:last-child {
      margin-bottom: 0;
    }
  }

  p:last-child {
    margin-bottom: 0;
  }

  li p {
    margin-bottom: 0;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.05);
    color: currentColor;
    padding: 6px;

    code {
      background-color: transparent !important;
    }
  }
}

.ProseMirror {
  @extend .rich-text;

  padding: 15px;
  min-height: 150px;
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}
</style>
